import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Swal from 'sweetalert2';
import SeoHead from '../components/SeoHead';
import {
  DRIVER_APP_NAME,
  DRIVER_ROUTES,
  DRIVER_SUPPORT,
} from '../data/driverAppSupport';
import '../styles/DriverLegal.css';

const DriverDataDeletion = () => {
  const [formData, setFormData] = useState({
    driverName: '',
    mobile: '',
    email: '',
    confirmDeletion: false,
  });
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const name = formData.driverName.trim();
    const mobile = formData.mobile.trim();
    const email = formData.email.trim();

    if (!name || !mobile) {
      Swal.fire({
        icon: 'error',
        title: 'Missing details',
        text: 'Please enter your driver name and registered mobile number.',
      });
      return;
    }

    if (!/^\d{10}$/.test(mobile)) {
      Swal.fire({
        icon: 'error',
        title: 'Invalid mobile number',
        text: 'Please enter the 10-digit registered mobile number used for the Driver App.',
      });
      return;
    }

    if (!formData.confirmDeletion) {
      Swal.fire({
        icon: 'warning',
        title: 'Confirmation required',
        text: 'Please confirm that you are requesting deletion of your EasyDropTaxi Driver account and applicable associated personal information.',
      });
      return;
    }

    setSubmitting(true);

    const subject = encodeURIComponent(
      `${DRIVER_APP_NAME} — Account & Data Deletion Request`,
    );
    const body = encodeURIComponent(
      [
        'EasyDropTaxi Driver App — Account & Data Deletion Request',
        '',
        `Driver Name: ${name}`,
        `Registered Mobile Number: ${mobile}`,
        `Email (if applicable): ${email || 'Not provided'}`,
        '',
        'I confirm that I am requesting deletion of my EasyDropTaxi Driver account and applicable associated personal information.',
        '',
        'Please verify this request and process it according to EasyDropTaxi policy.',
      ].join('\n'),
    );

    const mailtoUrl = `${DRIVER_SUPPORT.mailto}?subject=${subject}&body=${body}`;

    Swal.fire({
      icon: 'success',
      title: 'Request ready to send',
      html:
        `Your email app will open with the deletion request details.<br/><br/>` +
        `If it does not open, email <a href="${DRIVER_SUPPORT.mailto}">${DRIVER_SUPPORT.email}</a> ` +
        `or call <a href="${DRIVER_SUPPORT.phoneTel}">${DRIVER_SUPPORT.phoneDisplay}</a>.`,
    }).then(() => {
      window.location.href = mailtoUrl;
    }).finally(() => {
      setSubmitting(false);
    });
  };

  return (
    <main className="driver-legal-page">
      <SeoHead
        title="EasyDropTaxi Driver App Account & Data Deletion"
        description="Request deletion of your EasyDropTaxi Driver App account and applicable associated personal information. Public page for Google Play and driver support."
        path={DRIVER_ROUTES.dataDeletion}
      />

      <section className="page-banner block-space text-white" style={{ backgroundColor: 'var(--secondary-color)' }}>
        <div className="container">
          <h1 className="display-4 fw-bold">EasyDropTaxi Driver — Account &amp; Data Deletion</h1>
          <p className="lead mb-0">
            Public request page for the {DRIVER_APP_NAME} App. No Driver App login is required.
          </p>
        </div>
      </section>

      <section className="block-space">
        <div className="container">
          <div className="driver-legal-content">
            <p>
              Drivers can request deletion of their EasyDropTaxi Driver account and applicable
              associated personal information used in connection with the {DRIVER_APP_NAME} App.
            </p>

            <div className="driver-legal-note">
              <p>
                This page is publicly accessible and identifies the <strong>{DRIVER_APP_NAME}</strong> App.
                You do not need to install the App or remain logged in to submit a request.
              </p>
            </div>

            <h2>Request Process</h2>
            <p>
              Use the Account / Data Deletion Request Form below. The form collects only the
              information needed to identify and verify the driver account.
            </p>
            <p>
              You may also contact EasyDropTaxi support directly:
            </p>
            <div className="driver-contact-panel">
              <p>
                <strong>Email:</strong>{' '}
                <a href={DRIVER_SUPPORT.mailto}>{DRIVER_SUPPORT.email}</a>
              </p>
              <p className="mb-0">
                <strong>Phone:</strong>{' '}
                <a href={DRIVER_SUPPORT.phoneTel}>{DRIVER_SUPPORT.phoneDisplay}</a>
              </p>
            </div>

            <h2>Account / Data Deletion Request Form</h2>
            <form className="driver-deletion-form" onSubmit={handleSubmit} noValidate>
              <div className="mb-3">
                <label className="form-label" htmlFor="driverName">Driver Name *</label>
                <input
                  id="driverName"
                  name="driverName"
                  type="text"
                  className="form-control"
                  value={formData.driverName}
                  onChange={handleChange}
                  autoComplete="name"
                  required
                />
              </div>

              <div className="mb-3">
                <label className="form-label" htmlFor="mobile">Registered Mobile Number *</label>
                <input
                  id="mobile"
                  name="mobile"
                  type="tel"
                  className="form-control"
                  value={formData.mobile}
                  onChange={handleChange}
                  inputMode="numeric"
                  maxLength={10}
                  placeholder="10-digit mobile number"
                  autoComplete="tel"
                  required
                />
              </div>

              <div className="mb-3">
                <label className="form-label" htmlFor="email">Email (if applicable)</label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  className="form-control"
                  value={formData.email}
                  onChange={handleChange}
                  autoComplete="email"
                />
              </div>

              <div className="form-check mb-4">
                <input
                  id="confirmDeletion"
                  name="confirmDeletion"
                  type="checkbox"
                  className="form-check-input"
                  checked={formData.confirmDeletion}
                  onChange={handleChange}
                  required
                />
                <label className="form-check-label" htmlFor="confirmDeletion">
                  I confirm that I am requesting deletion of my EasyDropTaxi Driver account and
                  applicable associated personal information.
                </label>
              </div>

              <button
                type="submit"
                className="btn driver-btn-primary"
                disabled={submitting}
              >
                {submitting ? 'Preparing request...' : 'Submit Deletion Request'}
              </button>
            </form>

            <h2>Verification</h2>
            <p>
              EasyDropTaxi may verify the request before processing it. Verification may include
              confirming the registered mobile number or other account details associated with the
              Driver App.
            </p>

            <h2>Deletion</h2>
            <p>
              After verification, applicable personal information will be deleted or anonymized,
              subject to legitimate retention requirements described below.
            </p>

            <h2>Information That May Need Retention</h2>
            <p>
              Some information may need to be retained even after an account deletion request,
              where necessary for:
            </p>
            <ul>
              <li>Legal requirements</li>
              <li>Accounting / financial records</li>
              <li>Fraud prevention</li>
              <li>Security</li>
              <li>Dispute resolution</li>
              <li>Business record keeping</li>
              <li>Regulatory requirements</li>
            </ul>
            <p>
              EasyDropTaxi does not claim that every record will always be permanently deleted in
              every case.
            </p>

            <h2>Related Links</h2>
            <div className="driver-legal-actions">
              <Link to={DRIVER_ROUTES.privacy} className="btn driver-btn-outline">
                Driver Privacy Policy
              </Link>
              <Link to={DRIVER_ROUTES.support} className="btn driver-btn-primary">
                Driver Support
              </Link>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default DriverDataDeletion;
